import { useCallback, useState } from "react";
import { postEvent, useDrawerContext, useSnackbarContext } from "@WahooFitness/wahoo-offline-mfe";
import { t } from "@lingui/macro";
import useUserAccountSettings from "@/hooks/useUserAccountSettings";
import { ConnectedAppListItem } from "./useThirdPartySharing";
import { recordRecoverySourceUpdateError } from "./recoverySourceDebugStore";
import RecoverySourceChangeDrawer from "./RecoverySourceChangeDrawer";
import RecoverySourceTurnOffDrawer from "./RecoverySourceTurnOffDrawer";
import useRecoveryHealthScopesRequest from "./useRecoveryHealthScopesRequest";
import { clearPendingRecoverySourceAutoSelect } from "./recoverySourceAutoSelectPending";

// Tells native that `recovery_fitness_app_id` has just been written, with the new value
// (null when cleared). Handled by WFSettingsEventId.recoverySourceChanged on iOS.
export const RECOVERY_SOURCE_CHANGED_EVENT = "recoverySourceChanged";

// The active recovery source is a single account-level setting (recovery_fitness_app_id), so
// setting it to this app's fitness_app_id implicitly replaces whichever other app held it.
// `currentSourceName` names that other app in the change confirmation, when the caller knows it.
const useRecoverySourceToggle = (app: ConnectedAppListItem, currentSourceName?: string) => {
  const { fitnessAppId, content, nativeHealth } = app;
  const { userAccountSettingsData, updateUserAccountSettings } = useUserAccountSettings();
  const { enqueueSnackbar } = useSnackbarContext();
  const { setDrawer, handleClose: closeDrawer } = useDrawerContext();
  const { requestRecoveryHealthScopes } = useRecoveryHealthScopesRequest();
  const [switchDisabled, setSwitchDisabled] = useState(false);
  const isAppleHealth = !!nativeHealth;

  const activeFitnessAppId = userAccountSettingsData?.recovery_fitness_app_id;
  const isActive = !!fitnessAppId && activeFitnessAppId === fitnessAppId;

  const applyToggle = useCallback(async () => {
    if (!fitnessAppId) {
      return;
    }
    setSwitchDisabled(true);
    const requestedFitnessAppId = isActive ? null : fitnessAppId;
    // Emptying the slot on purpose ends any authorize-in-progress auto-selection.
    if (!requestedFitnessAppId) {
      void clearPendingRecoverySourceAutoSelect();
    }
    try {
      // Apple Health authorized export only, so enabling this asks native for recovery read scopes
      // and waits for the sheet to close; outcome unread — HealthKit never discloses grants.
      if (requestedFitnessAppId && isAppleHealth) {
        await requestRecoveryHealthScopes();
      }
      await updateUserAccountSettings({ recovery_fitness_app_id: requestedFitnessAppId });
      // This write is the moment the source changes, and we know the new value, so tell native now.
      // Native otherwise only learns it from a user sync, which can be minutes away - long enough for
      // a 90-day Apple Health backfill not to start while the user is still looking at the screen.
      // Fire-and-forget: nothing here waits on or reads a reply.
      // Every value has to be a string: the host decodes eventData as [String: String], so a number
      // or a null sinks the whole event at the decoder and it never arrives. "" is a cleared source.
      postEvent(RECOVERY_SOURCE_CHANGED_EVENT, {
        fitnessAppId: requestedFitnessAppId?.toString() ?? "",
      });
      // Announced here rather than by watching the source change, so it only ever follows a
      // deliberate toggle — watching fired on first load too, before any baseline existed.
      // Clearing the source stays silent; there's nothing to name.
      if (requestedFitnessAppId) {
        // Apple Health is the HRV source (TAJ-1628); every other provider keeps the recovery wording.
        enqueueSnackbar({
          message: isAppleHealth
            ? t`Your HRV data source has been changed to ${String(content)}.`
            : t`Your recovery source has been changed to ${String(content)}.`,
          severity: "success",
        });
      }
    } catch (error) {
      recordRecoverySourceUpdateError(requestedFitnessAppId, error);
      enqueueSnackbar({
        message: isAppleHealth
          ? t`An error occurred while updating your HRV data source. Please try again later.`
          : t`An error occurred while updating your recovery source. Please try again later.`,
        severity: "error",
      });
    }
    setSwitchDisabled(false);
  }, [
    content,
    enqueueSnackbar,
    fitnessAppId,
    isActive,
    isAppleHealth,
    requestRecoveryHealthScopes,
    updateUserAccountSettings,
  ]);

  const confirmThenApply = useCallback(
    (drawer: (confirm: () => void) => JSX.Element) => {
      setDrawer({
        open: true,
        hidePuller: true,
        hideClose: true,
        hideDivider: true,
        hideHeader: true,
        children: drawer(() => {
          closeDrawer();
          void applyToggle();
        }),
      });
    },
    [applyToggle, closeDrawer, setDrawer]
  );

  const handleToggle = useCallback(async () => {
    if (!fitnessAppId || switchDisabled) {
      return;
    }
    // Both paths that lose something confirm first: replacing another app's source,
    // and switching a source off — nothing auto-selects a replacement, so the recovery response
    // score stops even when another app is authorized. Only filling an empty slot just writes.
    if (!isActive && activeFitnessAppId && activeFitnessAppId !== fitnessAppId) {
      confirmThenApply((confirm) => (
        <RecoverySourceChangeDrawer
          currentSourceName={currentSourceName}
          newSourceName={String(content)}
          appleHealth={isAppleHealth}
          onConfirm={confirm}
          onCancel={closeDrawer}
        />
      ));
      return;
    }
    if (isActive) {
      confirmThenApply((confirm) => (
        <RecoverySourceTurnOffDrawer
          sourceName={String(content)}
          appleHealth={isAppleHealth}
          onConfirm={confirm}
          onCancel={closeDrawer}
        />
      ));
      return;
    }
    await applyToggle();
  }, [
    activeFitnessAppId,
    applyToggle,
    closeDrawer,
    confirmThenApply,
    content,
    currentSourceName,
    fitnessAppId,
    isActive,
    isAppleHealth,
    switchDisabled,
  ]);

  // Deauthorizing the active source leaves recovery_fitness_app_id pointing at an app the user
  // no longer shares from, so release it — silently, like any other clear. No-op otherwise.
  const releaseIfActive = useCallback(async () => {
    if (isActive) {
      await applyToggle();
    }
  }, [applyToggle, isActive]);

  return { isActive, switchDisabled, handleToggle, releaseIfActive };
};

export default useRecoverySourceToggle;
